<template>
  <div
    :id="componentId"
    :class="[
      'sam-text-list-item',
      { 'is-disabled': item.disabled },
      samStyleClass,
      { 'is-title': this.item.id === 'title' },
    ]"
    tabindex="-1"
    v-on="item.disabled ? {} : $listeners"
  >
    <SamLabel
      v-if="loading"
      loading
    />
    <template v-else>
      <slot name="prepend" />
      <div
        v-if="showCustomContent"
        class="sam-text-list-item__content-wrapper"
      >
        <div class="sam-text-list-item__content">
          <SamIcon
            v-if="item.icon"
            :icon="item.icon"
          />
          <span class="item__label text--ellipse">
            {{ item.label }}
          </span>
          <div
            v-if="item.hasNotification"
            class="item__notification-wrapper"
            :style="injectedStyles"
          >
            <span class="item__notification"></span>
          </div>
          <SamBadge
            v-if="item.badge != null"
            :value="item.badge"
            type="is-primary"
            class="item__badge"
          />
        </div>
        <div
          v-if="item.description"
          class="item__description__wrapper"
        >
          <span class="item__description">{{ item.description }}</span>
        </div>
      </div>
      <slot name="append"></slot>
    </template>
    <SamTooltip
      v-if="item.tooltip"
      :anchor-name="componentId"
      :message="item.tooltip"
      position="top"
    />
  </div>
</template>

<script>
import SamLabel from '../SamLabel/SamLabel.vue';
import SamBadge from '../SamBadge/SamBadge.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import SamTooltip from '../SamTooltip/SamTooltip.vue';
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import { uuidv4 } from '../../../utils/utils';

export const TEXT_LIST_ITEM_STYLES = {
  DANGER: SAMORA_STYLES.DANGER,
  SUCCESS: SAMORA_STYLES.SUCCESS,
  DEFAULT: SAMORA_STYLES.DEFAULT,
};

/**
 * The SamTextListItem object structure.
 * @typedef {Object} SamSelectListItem
 * @property {string} label - The item label.
 * @property {string} [id] - The item id.
 * @property {string} [badge] - The badge value.
 * @property {boolean} [hasNotification] - Adds notification badge.
 * @property {boolean} [disabled] - Adds disabled state to item.
 * @property {any} value - The item value.
 * @property {string} [icon] - The item icon to display.
 * @property {string} [samStyle] - The item samora style to apply.
 * @property {string} tooltip - The tooltip shown on item hover.
 */

export default {
  name: 'SamTextListItem',
  components: { SamBadge, SamIcon, SamLabel, SamTooltip },
  props: {
    /**
     * The item to be shown. It must follow a specific structure.
     * @type SamSelectListItem
     */
    item: {
      type: Object,
      required: false,
      default: () => ({
        label: '',
        badge: null,
        hasNotification: false,
        disabled: false,
        value: null,
        icon: null,
        samStyle: TEXT_LIST_ITEM_STYLES.DEFAULT,
        id: null,
        description: null,
        tooltip: null,
      }),
    },
    /**
     * Whether to set the list in a loading state
     * @values true, false
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Shows custom content
     * @values true, false
     */
    showCustomContent: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      componentId: `item--${this.item.id ?? uuidv4()}`,
    };
  },
  computed: {
    samStyleClass() {
      if (this.item.samStyle) {
        return `is-${this.item.samStyle}`;
      }
      return '';
    },
    injectedStyles() {
      return {
        '--notification-margin-left': this.item.badge || this.$slots.append ? '8px' : 'auto',
      };
    },
  },
};
</script>

<style scoped lang="scss">
.sam-text-list-item {
  @extend .p-4, .text--body-2, .d-inline-flex, .items-center;
  width: 100%;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  column-gap: $size--2;
  color: $pure--black;

  &:hover {
    background-color: $informative--surface;
  }

  &:not(.is-disabled):focus {
    &:after {
      @extend .elevation--2, .d-block;
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      border-radius: $size--1;
      outline: $size--1 solid $informative--border;
    }

    background-color: $informative--surface;
  }

  .item__notification-wrapper {
    position: relative;
    align-self: flex-start;
    width: $size--2;
    margin-left: var(--notification-margin-left);

    &:not(:last-child) {
      @extend .mr-2;
    }

    .item__notification {
      position: absolute;
      top: 0;
      left: 0;
      width: $size--2;
      height: $size--2;
      border-radius: $size--2;
      background-color: $informative--main;
    }
  }

  .item__badge {
    margin-left: auto;

    &:not(:last-child) {
      @extend .mr-2;
    }
  }

  .sam-text-list-item__content-wrapper {
    @extend .d-flex, .flex-column;
    row-gap: $size--1;
    width: 100%;

    .sam-text-list-item__content {
      @extend .d-inline-flex, .items-center;
      position: relative;
      column-gap: $size--2;
      width: 100%;
    }
  }

  .item__description__wrapper {
    width: 100%;

    .item__description {
      @extend .text--caption, .d-block;
      color: $neutral--80;
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.is-success {
    background-color: $informative--surface;
    color: $accent--unbabel-blue;
  }

  &.is-danger {
    color: $error--main;
  }

  &.is-disabled {
    cursor: not-allowed;
    background-color: $neutral--20;
    color: $neutral--60;
  }

  &.is-title {
    pointer-events: none;

    .item__label {
      @extend .text--body-2--medium;
      white-space: pre-line;
    }

    .item__description {
      white-space: pre-line;
    }
  }

  ::v-deep .sam-checkbox__input:checked + .sam-checkbox__label {
    color: $informative--main;
  }
}
</style>
